import React, { useEffect, useState } from "react";
import Select from "react-select";
import { toast, Toaster } from "react-hot-toast";
import { ArrowLeft, Download, Search, RefreshCw } from "lucide-react";
import * as XLSX from "xlsx";
import { getWeeklyPaymentReport } from "../../services/masterDataService";
import { selectStyles } from "../../helpers";
import "./WeeklyPaymentDetailsReport.css";

const pageSizeOptions = [
  { value: 25, label: "25 / page" },
  { value: 50, label: "50 / page" },
  { value: 100, label: "100 / page" },
];

const formatDate = (v) => (v ? new Date(v).toLocaleDateString("en-US") : "");
const money = (v) => (v == null || v === "" ? "" : Number(v).toFixed(2));

const WeeklyPaymentDetailsReport = ({
  domain,
  companyId,
  officeId,
  provider,
  fromDate,
  toDate,
  onBack,
}) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchText, setSearchText] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePage = Math.min(pageNumber, totalPages);

  const loadPage = async (page, size, search = appliedSearch) => {
    if (!domain || !companyId || !fromDate || !toDate) {
      toast.error("Missing filters. Go back and search again.");
      return;
    }
    setLoading(true);
    try {
      const data = await getWeeklyPaymentReport({
        domain,
        companyIds: companyId,
        fromDate,
        toDate,
        tabName: "Details",
        officeId: officeId || "",
        searchText: search || "",
        pageNumber: page,
        pageSize: size,
        isExport: false,
      });
      const body = data?.details || data?.data?.details ? (data.details ? data : data.data) : data;
      setRows(body.details || body.items || []);
      setTotalCount(body.totalCount || 0);
      setPageNumber(page);
      setPageSize(size);
      setAppliedSearch(search || "");
      if (!(body.details || body.items || []).length) toast("No data");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load details");
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPage(1, pageSize, "");
  }, [domain, companyId, officeId, fromDate, toDate]);

  const handleSearch = () => loadPage(1, pageSize, searchText.trim());

  const handleResetSearch = () => {
    setSearchText("");
    loadPage(1, pageSize, "");
  };

  const handleDownload = async () => {
    setLoading(true);
    try {
      const data = await getWeeklyPaymentReport({
        domain,
        companyIds: companyId,
        fromDate,
        toDate,
        tabName: "Details",
        officeId: officeId || "",
        searchText: appliedSearch || "",
        pageNumber: 1,
        pageSize: 25,
        isExport: true,
      });
      const body = data?.details ? data : data?.data || data;
      const list = body.details || body.items || [];
      if (!list.length) return toast.error("No data to download");
      if ((body.totalCount || 0) > list.length) {
        toast.error(`Export incomplete: ${list.length} of ${body.totalCount}`);
      }
      const mapped = list.map((r) => ({
        "Case #": r.caseNumber,
        Patient: `${r.patientFirstName || ""} ${r.patientLastName || ""}`.trim(),
        "Case Type": r.caseType,
        "Bill #": r.billNumber,
        "Bill Date": formatDate(r.billDate),
        "Visit Date": r.visitDate,
        "Check Date": formatDate(r.checkDate),
        "Check #": r.checkNumber,
        Posted: formatDate(r.paymentPostedDate),
        Doctor: r.doctor,
        Provider: r.provider,
        Location: r.location,
        Billed: money(r.billed),
        "Check Amount": money(r.checkAmount),
        Interest: money(r.interest),
        "Bill Paid": money(r.billPaid),
        "Write Off": money(r.writeOff),
        Balance: money(r.balance),
        Insurance: r.insurance,
        Specialty: r.specialty,
        "Bill Status": r.billStatus,
      }));
      const ws = XLSX.utils.json_to_sheet(mapped);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Details");
      XLSX.writeFile(wb, `WeeklyPayment_${officeId || "All"}.xlsx`);
      toast.success(`Exported ${list.length} rows`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Export failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="wpd-page">
      <Toaster position="top-right" />

      <div className="wpd-header">
        <button type="button" className="wpd-btn wpd-btn-back" onClick={onBack}>
          <ArrowLeft size={14} />
          Back
        </button>

        <div className="wpd-title">
          <h3>
            {provider || "Provider"} ({officeId})
          </h3>
          <span>
            {fromDate} → {toDate}
          </span>
        </div>
        
        <div className="wpd-search-row">
          <input
            className="wpd-input"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Search Case #, Bill #, Check #"
          />
          <button type="button" className="wpd-btn wpd-btn-back" onClick={handleSearch} disabled={loading}>
            <Search size={14} />
            Search
          </button>
          <button type="button" className="wpd-btn wpd-btn-reset" onClick={handleResetSearch} disabled={loading}>
            <RefreshCw size={14} />
            Reset
          </button>
        </div>

        <button type="button" className="wpd-btn wpd-btn-reset" onClick={handleDownload} disabled={loading || !rows.length}>
          <Download size={14} />
          Excel
        </button>
      </div>

      <div className="wpd-table-wrap">
        {loading && (
          <div className="wpd-loading">
            <span className="wpd-spinner" />
            Loading...
          </div>
        )}
        <table className="wpd-table">
          <thead>
            <tr>
              <th>Case #</th>
              <th>Patient</th>
              <th>Case Type</th>
              <th>Bill #</th>
              <th>Bill Date</th>
              <th>Visit Date</th>
              <th>Check Date</th>
              <th>Check #</th>
              <th>Posted</th>
              <th>Doctor</th>
              <th>Location</th>
              <th>Billed</th>
              <th>Check Amount</th>
              <th>Interest</th>
              <th>Bill Paid</th>
              <th>Write Off</th>
              <th>Balance</th>
              <th>Insurance</th>
              <th>Specialty</th>
              <th>Bill Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={20} className="wpd-empty">
                  {loading ? "" : "No payments"}
                </td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr key={`${r.billNumber}-${r.checkNumber}-${i}`}>
                  <td>{r.caseNumber}</td>
                  <td>{`${r.patientFirstName || ""} ${r.patientLastName || ""}`.trim()}</td>
                  <td>{r.caseType}</td>
                  <td>{r.billNumber}</td>
                  <td>{formatDate(r.billDate)}</td>
                  <td>{r.visitDate}</td>
                  <td>{formatDate(r.checkDate)}</td>
                  <td>{r.checkNumber}</td>
                  <td>{formatDate(r.paymentPostedDate)}</td>
                  <td>{r.doctor}</td>
                  <td>{r.location}</td>
                  <td>{money(r.billed)}</td>
                  <td>{money(r.checkAmount)}</td>
                  <td>{money(r.interest)}</td>
                  <td>{money(r.billPaid)}</td>
                  <td>{money(r.writeOff)}</td>
                  <td>{money(r.balance)}</td>
                  <td>{r.insurance}</td>
                  <td>{r.specialty}</td>
                  <td>{r.billStatus}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalCount > 0 && (
        <div className="wpd-pager">
          <Select
            options={pageSizeOptions}
            value={pageSizeOptions.find((o) => o.value === pageSize)}
            onChange={(opt) => loadPage(1, opt.value, appliedSearch)}
            styles={selectStyles}
            menuPlacement="top"
            isSearchable={false}
          />
          <span>
            {(safePage - 1) * pageSize + 1}-{Math.min(safePage * pageSize, totalCount)} of {totalCount}
          </span>
          <button
            type="button"
            className="wpd-btn wpd-btn-reset"
            disabled={safePage <= 1 || loading}
            onClick={() => loadPage(safePage - 1, pageSize, appliedSearch)}
          >
            Prev
          </button>
          <button
            type="button"
            className="wpd-btn wpd-btn-reset"
            disabled={safePage >= totalPages || loading}
            onClick={() => loadPage(safePage + 1, pageSize, appliedSearch)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default WeeklyPaymentDetailsReport;